import { MenuItem, Stack, TextField } from '@mui/material'

import type { FieldProps } from '@rjsf/utils'

import { useId } from 'react'

import { useTranslation } from 'react-i18next'

export type ChemicalBlackeningFormData = {
  treatmentOption?: 'One-sided' | 'Two-sided' | 'Full' | 'Selective'

  selectiveArea?: number

  areaUnit?: 'mm²' | 'm²'
}

const treatmentOptions = [
  'One-sided',

  'Two-sided',

  'Full',

  'Selective',
] as const

const areaUnits = ['mm²', 'm²'] as const

/** Edits one activity's treatment recipe while retaining inactive selective values. */

export function ChemicalBlackeningField(
  props: FieldProps<ChemicalBlackeningFormData>,
) {
  const id = useId()

  const { t } = useTranslation('project')

  const data = props.formData ?? {}

  const disabled = props.disabled || props.readonly

  const errors = props.errorSchema ?? {}

  const label = (key: string, fallback: string) =>
    t(key, { defaultValue: fallback })

  const change = (next: ChemicalBlackeningFormData) => props.onChange(next)

  const changeUnit = (unit: ChemicalBlackeningFormData['areaUnit']) => {
    let area = data.selectiveArea

    if (area != null && data.areaUnit && data.areaUnit !== unit) {
      area = data.areaUnit === 'mm²' ? area / 1_000_000 : area * 1_000_000
    }

    change({ ...data, areaUnit: unit, selectiveArea: area })
  }

  return (
    <Stack spacing={2}>
      <TextField
        id={`${id}-treatment`}
        select
        fullWidth
        size="small"
        required
        disabled={disabled}
        label={label('ws-form-treatment-option', 'Treatment option')}
        value={data.treatmentOption ?? ''}
        error={!!errors.treatmentOption?.__errors?.length}
        helperText={errors.treatmentOption?.__errors?.join(' ')}
        onChange={(event) =>
          change({
            ...data,

            treatmentOption: event.target
              .value as ChemicalBlackeningFormData['treatmentOption'],
          })
        }
      >
        {treatmentOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {label(`ws-form-treatment-${option.toLowerCase()}`, option)}
          </MenuItem>
        ))}
      </TextField>

      {data.treatmentOption === 'Selective' && (
        <>
          <TextField
            id={`${id}-area`}
            fullWidth
            size="small"
            type="number"
            required
            disabled={disabled}
            label={label(
              'ws-form-treated-surface-area-per-item',

              'Treated surface area per item',
            )}
            value={data.selectiveArea ?? ''}
            error={!!errors.selectiveArea?.__errors?.length}
            helperText={errors.selectiveArea?.__errors?.join(' ')}
            onChange={(event) =>
              change({
                ...data,

                selectiveArea:
                  event.target.value === ''
                    ? undefined
                    : Number(event.target.value),
              })
            }
          />

          <TextField
            id={`${id}-unit`}
            select
            fullWidth
            size="small"
            required
            disabled={disabled}
            label={label('ws-form-surface-area-unit', 'Surface area unit')}
            value={data.areaUnit ?? ''}
            error={!!errors.areaUnit?.__errors?.length}
            helperText={errors.areaUnit?.__errors?.join(' ')}
            onChange={(event) =>
              changeUnit(
                event.target.value as ChemicalBlackeningFormData['areaUnit'],
              )
            }
          >
            {areaUnits.map((unit) => (
              <MenuItem key={unit} value={unit}>
                {unit}
              </MenuItem>
            ))}
          </TextField>
        </>
      )}
    </Stack>
  )
}
